import React, { useCallback, useEffect, useState } from "react";
import { Select, Typography } from "antd";
import SVGIcon from "../../constants/SVGIcon";
import CustomTextfield from "../../common/input/custom-textfield/custom-textfield";
import CustomTable from "../../common/custom-table/custom-table";
import { getProductDemoRequests, updateProductDemoRequest } from "../../services/product_demo_requests";
import { productDemoRequestTypes } from "../../constants/common";
import { formatDateToShortReadable } from "../../utils/format-date/format-date";
import debounce from "lodash.debounce";
import ViewDemoRequest from "./components/view-demo-request/view-demo-request";
import CustomSelect from "../../common/input/custom-select/custom-select";
import { toast } from "react-toastify";
import CustomButton from "../../common/custom-button/custom-button";
import styles from "./product-demo-requests.module.css";

const { Option } = Select;

const ProductDemoRequests = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedFilter, setSelectedFilter] = useState("ALL");
  const [searchFieldValue, setSearchFieldValue] = useState("");
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [productDemoList, setProductDemoList] = useState([]);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [selectedDemoData, setSelectedDemoData] = useState({});

  const onStatusChange = async (status, record) => {
    setLoading(true);
    try {
      const id = record?.id;
      const payload = { id, status };
      await updateProductDemoRequest(id, payload);
      toast.success("Demo request updated successufully!");
      getProductDemoList();
    } catch (error) {
      toast.error(error?.data?.message || "Something went wrong");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: "Institution name",
      dataIndex: "institution_name",
      key: "institution_name",
      render: (value) => value || "-",
    },
    {
      title: "Requester name",
      dataIndex: "full_name",
      key: "full_name",
      align: "center",
      render: (value) => value || "-",
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      align: "center",
      render: (value) => value || "-",
    },
    {
      title: "Phone No.",
      dataIndex: "phone_number",
      key: "phone_number",
      align: "center",
      render: (value) => value || "-",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      align: "center",
      render: (value, record) => (
        <div className={styles.selectInputWrapper}>
          <CustomSelect
            id="status"
            placeholder="--Select--"
            size="large"
            value={value?.toUpperCase()}
            onChange={(e) => onStatusChange(e, record)}
            className={styles.statusSelect}
          >
            {productDemoRequestTypes.map((item, index) => (
              <Option key={index} value={item?.value}>
                <span style={{ color: item?.color }}>{item?.label}</span>
              </Option>
            ))}
          </CustomSelect>
        </div>
      )
    },
    {
      title: "Date Requested",
      dataIndex: "created_at",
      key: "created_at",
      align: "center",
      render: (date) => formatDateToShortReadable(date),
    },
    {
      title: "Action",
      key: "action_child",
      align: "center",
      render: (_, record) => (
        <div className={styles.viewDetailsWrapper}>
          <CustomButton
            title="View Details"
            size="small"
            onClick={() => handleViewDetails(record)}
            className={styles.viewDetailsBtn}
          />
        </div>
      ),
    },
  ];

  const debouncedSearch = useCallback(
    debounce((search_term) => {
      setSearchTerm(search_term);
    }, 500),
    []
  );

  const handleSearchChange = (e) => {
    setSelectedFilter("ALL");
    setSearchFieldValue(e.target.value);
    debouncedSearch(e.target.value);
  };

  const handleChangeFilterValue = (value) => {
    setSelectedFilter(value);
    setSearchFieldValue("");
    const lower_cased_filter_value = value?.toLowerCase()?.replace(" ", "");
    if (lower_cased_filter_value === "all") {
      setSearchTerm("");
      return;
    }
    setSearchTerm(lower_cased_filter_value);
  };

  useEffect(() => {
    const timeOut = setTimeout(() => {
      setPageNo(1);
      getProductDemoList();
    }, 500);
    return () => {
      clearTimeout(timeOut);
    };
  }, [searchTerm]);

  const getProductDemoList = async (
    tempPage = pageNo,
    tempPageSize = pageSize
  ) => {
    try {
      setLoading(true);
      const payload = {
        page: tempPage,
        pageSize: tempPageSize,
      };

      if (searchTerm) {
        payload.search = searchTerm;
      }

      const response = await getProductDemoRequests(payload);
      if (response?.data) {
        const consolidated_list = response?.data?.data?.demoRequests.map(
          (item) => ({
            ...item,
            role: `${item?.role[0]?.toUpperCase()}${item?.role
              ?.slice(1)
              ?.toLowerCase()}`,
            status: `${item?.status[0]?.toUpperCase()}${item?.status
              ?.slice(1)
              ?.toLowerCase()}`,
          })
        );
        setProductDemoList(consolidated_list);
        setTotalCount(response?.data?.data?.totalRecords);
      }
    } catch (error) {
      console.log("error", error);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (page, pageSize) => {
    setPageNo(page);
    setPageSize(pageSize);
    getProductDemoList(page, pageSize);
  };
  
  const handleCloseDrawer = () => {
    setOpenDrawer(false);
    setSelectedDemoData(null);
  };

  const handleViewDetails = (record) => {
    setOpenDrawer(true);
    setSelectedDemoData(record);
  }
 
  return (
    <div>
      <div className={styles.headerContainer}>
        <Typography className={styles.title}>Product Demo Requests</Typography>
        <div className={styles.filterContainer}>
          <CustomTextfield
            id={"search"}
            type={"text"}
            className={styles.inputField}
            prefix={
              <SVGIcon
                name={"search-icon"}
                width={"20px"}
                height={"20px"}
                className="anticon"
                stroke={"#667085"}
              />
            }
            placeholder={"Search"}
            allowClear
            value={searchFieldValue}
            onChange={handleSearchChange}
          />
          <Select
            className={styles.select_filter}
            name="filter"
            placeholder="All"
            value={selectedFilter}
            onChange={handleChangeFilterValue}
          >
            <Option key="all" value="ALL">
              All
            </Option>
            {productDemoRequestTypes.map((item, index) => (
              <Option key={index} value={item?.value}>
                {item?.label}
              </Option>
            ))}
          </Select>
        </div>
      </div>

      <div className={styles.tableContainer}>
        <CustomTable
          columns={columns}
          dataSource={productDemoList}
          total={totalCount}
          loading={loading}
          pageSize={pageSize}
          currentPage={pageNo}
          handlePageChange={handleChangePage}
        />
      </div>

      {openDrawer && (
        <ViewDemoRequest
          open={openDrawer}
          onClose={handleCloseDrawer} 
          selectedDemoData={selectedDemoData}
        />
      )}
    </div>
  );
};

export default ProductDemoRequests;
